import { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Users, GraduationCap, Building2, Heart, Check, ArrowRight, Mail, Phone } from 'lucide-react';

export function Partnerships() {
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationType: 'college',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    userCount: '',
    message: '',
  });

  const partnerTypes = [
    {
      icon: GraduationCap,
      name: 'Colleges & Universities',
      description: 'Equip every student with an AI career coach',
      benefits: [
        'Bulk access for 500-5,000 students',
        'Custom onboarding & training',
        'Progress tracking dashboard',
        'Dedicated support team',
        'Success metrics & reporting',
      ],
      pricing: '$2-$10 per user/month',
      color: 'cyan',
    },
    {
      icon: Users,
      name: 'Workforce Centers',
      description: 'Transform job seekers into success stories',
      benefits: [
        'Multi-site deployment',
        'Case management integration',
        'Outcome tracking tools',
        'Staff training included',
        'White-label options available',
      ],
      pricing: '$3-$8 per user/month',
      color: 'blue',
    },
    {
      icon: Heart,
      name: 'Nonprofits & Community Programs',
      description: 'Bring opportunity to every community',
      benefits: [
        'Discounted nonprofit pricing',
        'Flexible payment terms',
        'Community impact reporting',
        'Partnership co-marketing',
        'Priority Detroit-area access',
      ],
      pricing: '$1-$5 per user/month',
      color: 'orange',
    },
    {
      icon: Building2,
      name: 'Reentry & Youth Programs',
      description: 'Support meaningful career transitions',
      benefits: [
        'Specialized content library',
        'Trauma-informed design',
        'Mentorship matching',
        'Skills assessment tools',
        'Long-term outcome tracking',
      ],
      pricing: '$2-$6 per user/month',
      color: 'teal',
    },
  ];

  const caseStudies = [
    {
      partner: 'Detroit Community College',
      logo: GraduationCap,
      users: 3500,
      stat: '78% placement rate',
      quote: 'ResuSwipe helped our students land jobs 40% faster than traditional methods.',
    },
    {
      partner: 'Motor City Workforce',
      logo: Users,
      users: 1200,
      stat: '92% satisfaction',
      quote: 'The AI coaching gives our clients confidence they never had before.',
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Partnership inquiry submitted:', formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-full mb-6">
            <Users className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-semibold text-orange-900">Bulk Licensing & Partnerships</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Partners in Opportunity
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Equip every learner with an AI career coach. Starting at $2/user/month.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {partnerTypes.map((type) => {
            const Icon = type.icon;
            return (
              <div
                key={type.name}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
              >
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${
                    type.color === 'cyan'
                      ? 'from-cyan-500 to-blue-500'
                      : type.color === 'blue'
                      ? 'from-blue-500 to-cyan-500'
                      : type.color === 'orange'
                      ? 'from-orange-500 to-amber-500'
                      : 'from-teal-500 to-cyan-500'
                  } rounded-xl flex items-center justify-center mb-4`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.name}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>

                <div className="mb-6">
                  <div className="text-2xl font-bold text-gray-900">{type.pricing}</div>
                  <div className="text-sm text-gray-500">Volume discounts available</div>
                </div>

                <div className="space-y-2.5 mb-6">
                  {type.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-cyan-500" />
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <button className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-600 transition-all">
                  Request Pricing
                </button>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-10 mb-20 text-white">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold mb-4">Detroit-First Promise</h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              We're committed to bringing opportunity to every Detroit community. Special pricing and priority access for Detroit-area organizations.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl font-bold mb-2">50%</div>
              <div className="text-sm">Discount for Detroit nonprofits</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl font-bold mb-2">Priority</div>
              <div className="text-sm">Access to new features</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-3xl font-bold mb-2">Free</div>
              <div className="text-sm">Implementation & training</div>
            </div>
          </div>
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Partner Success Stories
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study) => {
              const Logo = study.logo;
              return (
                <div
                  key={study.partner}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                      <Logo className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{study.partner}</h3>
                      <p className="text-sm text-gray-600">{study.users.toLocaleString()} users</p>
                    </div>
                  </div>

                  <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-4 mb-4">
                    <div className="text-3xl font-bold text-cyan-900 mb-1">{study.stat}</div>
                  </div>

                  <p className="text-gray-700 italic">"{study.quote}"</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-10 border-2 border-gray-200">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            Start a Partnership
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Tell us about your organization and we'll create a custom solution
          </p>

          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                  placeholder="Your Organization"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Organization Type *
                </label>
                <select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                >
                  <option value="college">College/University</option>
                  <option value="workforce_center">Workforce Center</option>
                  <option value="nonprofit">Nonprofit</option>
                  <option value="reentry_program">Reentry Program</option>
                  <option value="youth_program">Youth Program</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Email *
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                  placeholder="john@organization.org"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Expected User Count *
                </label>
                <input
                  type="number"
                  name="userCount"
                  value={formData.userCount}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                  placeholder="500"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tell us about your goals
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-cyan-500 focus:outline-none"
                placeholder="What outcomes are you hoping to achieve for your community?"
              />
            </div>

            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              Submit Partnership Inquiry
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="text-center text-sm text-gray-500 mt-4">
              We'll respond within 1 business day with custom pricing and next steps
            </p>
          </form>
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">Questions? We're here to help.</p>
          <div className="flex items-center justify-center gap-6">
            <a
              href="mailto:partnerships@resuswipe.com"
              className="flex items-center gap-2 text-cyan-600 font-semibold hover:text-cyan-700 transition"
            >
              <Mail className="w-5 h-5" />
              partnerships@resuswipe.com
            </a>
            <span className="text-gray-400">•</span>
            <a
              href="tel:+15551234567"
              className="flex items-center gap-2 text-cyan-600 font-semibold hover:text-cyan-700 transition"
            >
              <Phone className="w-5 h-5" />
              (555) 123-4567
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
